import { inject, Injectable } from '@angular/core';
import { SharedMasterDataProductPropertiesSchema } from '@libs/shared/domain/master-data/product/shared-master-data-product.properties.schema';
import { HttpService } from '@services/http.service';
import { Product } from '@services/product.model';
import { TenantService } from '@services/tenant.service';
import { UserService } from '@services/user.service';
import { SelectListItem } from '@shared/models/select-list-item';
import { chain } from 'lodash';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ProductsService extends HttpService {
  private tenantService = inject(TenantService);
  private userService = inject(UserService);

  get(ndc: string) {
    return this.httpClient.get<{ product: SharedMasterDataProductPropertiesSchema }>(`${this.urlService.PRODUCT_URL}/${ndc}`).pipe(
      catchError(error => this.handleGetError(error)),
      map(result => (result?.product ? this.mapProduct(result?.product) : null)),
    );
  }

  getByNdcs(ndcs: string[]) {
    return this.httpClient
      .post<{ products: SharedMasterDataProductPropertiesSchema[] }>(`${this.urlService.PRODUCT_URL}/by-ndcs`, { ndcs })
      .pipe(
        map(result => this.mapProducts(result.products, false)),
        catchError(error => this.handleGetListError<Product[]>(error)),
      );
  }

  getList(tenantId: string) {
    return this.getListOrThrow(tenantId).pipe(catchError(error => this.handleGetListError<Product[]>(error)));
  }

  getListOrThrow(tenantId: string): Observable<Product[]> {
    return this.httpClient
      .get<{ products: SharedMasterDataProductPropertiesSchema[] }>(`${this.urlService.PA_SERVICE}/tenant-products/${tenantId}`)
      .pipe(map(response => this.mapProducts(response.products)));
  }

  getAll(): Observable<Record<string, Product[]>> {
    const tenantIds = this.tenantService
      .allTenants()
      .map(tenant => tenant.id)
      .filter(tenantId => this.userService.hasAccessToTenant(tenantId));
    if (tenantIds.length === 0) return of({});
    return forkJoin(tenantIds.map(tenantId => this.getList(tenantId).pipe(map(products => ({ [tenantId]: products }))))).pipe(
      map(results => Object.assign({}, ...results)),
    );
  }

  getAllFlattened() {
    return this.httpClient.get<{ products: SharedMasterDataProductPropertiesSchema[] }>(this.urlService.PRODUCT_URL).pipe(
      map(result => this.mapProducts(result.products)),
      catchError(error => this.handleGetListError<Product[]>(error)),
    );
  }

  createProduct(product: SharedMasterDataProductPropertiesSchema) {
    return this.httpClient
      .post<{ product: SharedMasterDataProductPropertiesSchema }>(this.urlService.PRODUCT_URL, product)
      .pipe(map(result => this.mapProduct(result.product)));
  }

  getSelectList(tenantId: string): Observable<SelectListItem[]> {
    return this.getList(tenantId).pipe(map(products => this.mapSelectList(products)));
  }

  mapSelectList(products: Product[]): SelectListItem[] {
    return chain(products)
      .map(product => ({ name: product.name, value: product.ndc }))
      .value();
  }

  private mapProduct(product: SharedMasterDataProductPropertiesSchema): Product {
    return { ...product, name: product.label || 'UNKNOWN' };
  }

  private mapProducts(products: SharedMasterDataProductPropertiesSchema[], orderByName = true): Product[] {
    const mappedProducts = chain(products)
      .filter(product => product.ndc !== '16110024430x')
      .map(product => this.mapProduct(product));
    if (orderByName) return mappedProducts.sortBy(product => product.name.toLowerCase()).value();
    return mappedProducts.value();
  }
}
